/**
 * Pause & Resume (SCOPE_STAGE2 Punkt 9, A8): vollstaendiger RunState als JSON,
 * inkl. aller RNG-Counter. Store-Interface ohne localStorage-Leak nach aussen —
 * Stufe 3 tauscht die Implementierung gegen SpacetimeDB (docs/09 §6).
 */
import type { RunState } from './types'
import { RUN_STATE_VERSION } from './types'

export interface RunStateStore {
  save(state: RunState): void
  load(): RunState | null
  clear(): void
}

export function serializeRun(state: RunState): string {
  return JSON.stringify(state)
}

export function deserializeRun(json: string): RunState | null {
  try {
    const state = JSON.parse(json) as RunState
    if (typeof state !== 'object' || state === null) return null
    if (state.version !== RUN_STATE_VERSION) return null // Migrationspfad kommt bei Bedarf
    // strukturelle Minimal-Validierung (Review-Fix C10): kaputte Saves frueh ablehnen
    if (typeof state.masterSeed !== 'number' || typeof state.round !== 'number') return null
    if (typeof state.hp !== 'number' || typeof state.gold !== 'number') return null
    if (!['focusPick', 'shop', 'result', 'ended'].includes(state.phase)) return null
    if (!Array.isArray(state.champions) || !Array.isArray(state.weapons)) return null
    if (!Array.isArray(state.passives) || !Array.isArray(state.fractures)) return null
    if (typeof state.shop !== 'object' || state.shop === null || !Array.isArray(state.shop.slots)) return null
    if (typeof state.rngCounters !== 'object' || state.rngCounters === null) return null
    if (typeof state.config !== 'object' || state.config === null || typeof state.config.grid !== 'object') return null
    return state
  } catch {
    return null
  }
}

const STORAGE_KEY = 'autobattler.run.v1'

/** v0: Browser localStorage, ein Run pro Browser (A8) */
export class LocalStorageStore implements RunStateStore {
  save(state: RunState): void {
    localStorage.setItem(STORAGE_KEY, serializeRun(state))
  }
  load(): RunState | null {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? deserializeRun(raw) : null
  }
  clear(): void {
    localStorage.removeItem(STORAGE_KEY)
  }
}

/** fuer Tests / Node-Umgebung */
export class MemoryStore implements RunStateStore {
  private data: string | null = null
  save(state: RunState): void {
    this.data = serializeRun(state)
  }
  load(): RunState | null {
    return this.data ? deserializeRun(this.data) : null
  }
  clear(): void {
    this.data = null
  }
}
